import React, { useContext, useEffect } from 'react';
import { Form, Card, Input, Button, message, Tooltip, Select, Divider, Alert } from 'antd';
import { useMutation } from '@apollo/client';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { COUNTRIES } from '../../../utils/constants';

import gql from 'graphql-tag';
import ProgressModal from '../../../components/ProgressModal';
import PageTitle from '../../../components/PageTitle';
import AppContext from '../../../components/AppContext';
import Pullable from '../../../components/Pullable';

const UPDATE_USER_MUTATION = gql`
  mutation UpdateUserMutation($id: ID!, $user: UserInput!) {
    updateUser(id: $id, user: $user) {
      id
      email
      code
      name
      wechatId
      country
      city
      state
      zipCode
      recipientName
      phone
      address
      operator {
        id
        name
        wechatId
        phone
      }
    }
  }
`;

type FormType = {
  email: string,
  name: string,
  wechatId: string,
  recipientName: string,
  phone: string,
  country: string,
  address: string,
  city: string,
  state: string,
  zipCode: string,
};

function EditProfile() {
  const [form] = Form.useForm<FormType>();
  const { updateAuthInfo, refetchUserInfo, user } = useContext(AppContext);
  useEffect(() => {
    if (user.id) {
      form.resetFields();
    }
  }, [user, form]);

  const [updateUser] = useMutation(UPDATE_USER_MUTATION, {
    onError: () => null,
    onCompleted: data => {
      ProgressModal.hide();

      if (data.updateUser?.name !== user.name) {
        message.success('修改成功，别忘了改电商网站收货地址里的名字');
      } else {
        message.success('修改成功');
      }

      saveUserData(data.updateUser);
    },
  });
  const onEditProfile = (values: FormType) => {
    ProgressModal.show();

    updateUser({ variables: { user: values, id: user.id } });
  };

  const resetForm = () => {
    form.resetFields();
  };

  const saveUserData = user => {
    updateAuthInfo({ user });
  };

  return (
    <div className="EditProfile">
      <PageTitle>账户信息</PageTitle>
      <Pullable onRefresh={refetchUserInfo}>
        <Card bordered={false}>
          <Form
            form={form}
            layout="vertical"
            onFinish={onEditProfile}
            scrollToFirstError
            initialValues={user}
          >
            <Form.Item label="会员号">
              <span style={{ color: '#EB6841', fontSize: '1.1rem' }}>{user.code}</span>
            </Form.Item>
            <Form.Item label="操作员">{(user.operator || {}).name} </Form.Item>

            <Form.Item
              label="邮箱"
              name="email"
              validateTrigger="onBlur"
              rules={[
                { required: true, message: '必填' },
                { type: 'email', message: '邮箱格式有误' },
              ]}
            >
              <Input allowClear autoComplete="username" />
            </Form.Item>
            <Form.Item
              label="名字（真实姓名或昵称或微信名）"
              name="name"
              validateTrigger="onBlur"
              rules={[{ required: true, message: '必填' }]}
            >
              <Input allowClear autoComplete="username" />
            </Form.Item>

            <Form.Item
              label={
                <span>
                  微信号（注意不是微信名）&nbsp;
                  <Tooltip title="为了方便操作员通过微信联系你，请提供微信号。注意是微信号不是微信名字，在微信里可以通过：点界面右下角的“我” -> 点自己头像来查看微信号">
                    <QuestionCircleOutlined />
                  </Tooltip>
                </span>
              }
              name="wechatId"
              rules={[{ required: true, message: '必填' }]}
            >
              <Input allowClear autoComplete="name" />
            </Form.Item>

            <Divider plain>收货地址</Divider>
            <Alert type="warning" message="请用目的国家的语言填写" style={{ marginBottom: '8px' }} />

            <Form.Item
              label="收件人名字"
              name="recipientName"
              rules={[{ required: true, message: '必填' }]}
            >
              <Input allowClear />
            </Form.Item>
            <Form.Item label="联系电话" name="phone" rules={[{ required: true, message: '必填' }]}>
              <Input allowClear />
            </Form.Item>
            <Form.Item label="目的国家" name="country" rules={[{ required: true, message: '必填' }]}>
              <Select>
                {Object.entries(COUNTRIES).map(([countryCode, countryName]) => (
                  <Select.Option value={countryCode} key={countryCode}>
                    {countryName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item label="街道地址" name="address" rules={[{ required: true, message: '必填' }]}>
              <Input allowClear />
            </Form.Item>

            <Form.Item label="城市" name="city" rules={[{ required: true, message: '必填' }]}>
              <Input allowClear />
            </Form.Item>

            <Form.Item label="州/省" name="state" rules={[{ required: true, message: '必填' }]}>
              <Input allowClear />
            </Form.Item>

            <Form.Item
              label="邮编"
              name="zipCode"
              rules={[
                { required: true, message: '必填' },
                // { type: 'string', pattern: /^[0-9]+$/, len: 5, message: '邮编不合法'},
              ]}
            >
              <Input allowClear />
            </Form.Item>

            <Form.Item>
              <Button block type="primary" htmlType="submit">
                保存修改
              </Button>
            </Form.Item>

            <Form.Item style={{ textAlign: 'center' }}>
              <Button type="link" htmlType="button" onClick={resetForm}>
                取消修改
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </Pullable>
    </div>
  );
}

export default EditProfile;
